import React, { useMemo, useState, useEffect } from 'react';
import { UserData, ZodiacSign, AppSettings } from '../types';
import { calculateZodiac, calculateMoonSign, calculateRisingSign } from '../utils/calculator';
import { getCurrentSeasonSign, getTimeUntilAvailable } from '../utils/cosmic';
import { useSetMeta } from '../hooks/useSetMeta';
import ZodiacWheel from '../components/ZodiacWheel';
import CooldownTimer from '../components/CooldownTimer';
import { ZODIAC_ORDER, ZODIAC_DATA } from '../constants';
import { Heart, Briefcase, Users, Loader2, Sparkles, Clock, Crown, Lock, Check, ChevronDown, Share2, ArrowRight, Star } from 'lucide-react';
import { generateAIContent } from '../services/ai';
import AdSpace from '../components/AdSpace';
import zodiacData from '../src/content/zodiac.json';

interface ZodiacPageProps {
  userData: UserData;
  settings?: AppSettings;
    updateSettings?: (s: Partial<AppSettings>) => void;
}

const ZodiacPage: React.FC<ZodiacPageProps> = ({ userData, settings, updateSettings }) => {
  // Set SEO meta tags for this page
  useSetMeta({
    title: 'Zodiac Sign Calculator & Compatibility - Your Cosmic Chart | ZodiAge',
    description: 'Discover your zodiac sun, moon, and rising signs. Get instant zodiac compatibility analysis with detailed love, work, and friendship insights.',
    ogImage: 'https://zodiage.com/logo.png'
  });

  const [partnerSign, setPartnerSign] = useState<ZodiacSign | ''>('');
  const [isPartnerDropdownOpen, setIsPartnerDropdownOpen] = useState(false);
  const [loadingCompat, setLoadingCompat] = useState(false);
  const [compatibility, setCompatibility] = useState<{love: string, work: string, friend: string} | null>(null);
  const [weeklyForecast, setWeeklyForecast] = useState<string>('');
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  const handleShareSection = async (sectionName: string, textToCopy: string) => {
      if (navigator.share) {
          try {
              await navigator.share({
                  title: 'ZodiAge Compatibility',
                  text: textToCopy,
                  url: window.location.href
              });
              return;
          } catch (err) {
              // Fallback to clipboard if share fails or is cancelled
          }
      }

      navigator.clipboard.writeText(textToCopy).then(() => {
          setCopiedSection(sectionName);
          setTimeout(() => setCopiedSection(null), 2000);
      });
  };
  
  // Timer State
  const [compatTimer, setCompatTimer] = useState('');
  const [isCompatLocked, setIsCompatLocked] = useState(false);

  // Memoize calculations to prevent re-calc on every render
  const userSign = useMemo(() => {
    return userData.birthDate ? calculateZodiac(userData.birthDate) : null;
  }, [userData.birthDate]);

  const moonSign = useMemo(() => {
    return userData.birthDate ? calculateMoonSign(userData.birthDate) : null;
  }, [userData.birthDate]);

  const risingSign = useMemo(() => {
    return userData.birthDate && userData.includeTime 
        ? calculateRisingSign(userData.birthDate) 
        : null; 
  }, [userData.birthDate, userData.includeTime]);

  // Initialize/Restore Compatibility State & Timer Loop
  useEffect(() => {
      if (settings) {
          const status = getTimeUntilAvailable(settings.lastCompatibilityTimestamp || null);
          
          if (status.locked) {
              setIsCompatLocked(true);
              setCompatTimer(status.text);
              if (settings.lastCompatibilityResult) {
                  setCompatibility(settings.lastCompatibilityResult);
              }
          }
      }

      const updateTimer = () => {
          const status = getTimeUntilAvailable(settings?.lastCompatibilityTimestamp || null);
          if (status.locked) {
              setCompatTimer(status.text);
              setIsCompatLocked(true);
          } else {
              setCompatTimer('');
              setIsCompatLocked(false);
          }
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
  }, [settings?.lastCompatibilityTimestamp, settings?.lastCompatibilityResult]);

  // Load Weekly Forecast
  useEffect(() => {
      if (userSign && !weeklyForecast) {
          loadForecast();
      }
  }, [userSign]);

  const loadForecast = async () => {
      if (!userSign) return;
      setLoadingForecast(true);
      try {
          const prompt = `Weekly horoscope for ${userSign}. Calm, empowering tone. 2 short sentences focusing on growth.`;
          const responseText = await generateAIContent(prompt);
          if (responseText) setWeeklyForecast(responseText);
      } catch (e) {
          setWeeklyForecast("The stars align in mysterious ways this week.");
      } finally {
          setLoadingForecast(false);
      }
  };

  const handleCheckCompatibility = async () => {
      if (!userSign || !partnerSign) return;
      
    const status = getTimeUntilAvailable(settings?.lastCompatibilityTimestamp || null);
      if (status.locked) return;

      setLoadingCompat(true);
      setCompatibility(null);

      try {
          const prompt = `
            Analyze compatibility between ${userSign} and ${partnerSign}.
            Return exactly 3 short sentences separated by pipes (|).
            Sentence 1: Love compatibility (Fun & Flirty).
            Sentence 2: Work/Career synergy.
            Sentence 3: Friendship vibe.
            No preamble.
          `;
          
          const responseText = await generateAIContent(prompt);

          if (responseText) {
              const parts = responseText.split('|');
              let newResult = null;
              if (parts.length === 3) {
                  newResult = {
                      love: parts[0].trim(),
                      work: parts[1].trim(),
                      friend: parts[2].trim()
                  };
              } else {
                  newResult = {
                      love: "Sparks definitely fly between these two!",
                      work: "A power couple if goals align.",
                      friend: "Adventures await you both."
                  };
              }
              setCompatibility(newResult);
              
              if (updateSettings && newResult) {
                  updateSettings({
                      lastCompatibilityTimestamp: Date.now(),
                      lastCompatibilityResult: newResult
                  });
              }
          }
      } catch (e) {
          // Silent fail
      } finally {
          setLoadingCompat(false);
      }
  };

  // Show prompt to calculate age when no birth date
  if (!userSign) {
    return (
      <div className="flex flex-col items-center w-full min-h-[80vh] justify-center overflow-x-hidden">
        <div className="w-full relative flex flex-col items-center justify-center">
          {/* Prompt Card */}
          <div className="w-full max-w-xl mx-auto px-4 mb-12 animate-in fade-in duration-700">
            <div className="bg-card/30 border border-gray-800 rounded-3xl p-8 md:p-12 backdrop-blur-sm text-center">
              <div className="bg-gradient-to-r from-purple-600/20 to-pink-500/20 p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                <Star className="text-purple-400" size={40} />
              </div>
              <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
                Zodiac Compatibility
              </h1>
              <p className="text-gray-300 text-lg mb-3">
                Discover your Sun, Moon, and Rising signs.
              </p>
              <p className="text-gray-400 text-sm mb-8 leading-relaxed">
                To unlock the Cosmic Compatibility tool and see your personalized zodiac wheel, 
                you'll need to calculate your age first.
              </p>
              <div className="flex justify-center">
                <button 
                  onClick={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}
                  className="bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white font-bold py-3.5 px-8 rounded-full hover:from-purple-500 hover:via-purple-400 hover:to-pink-400 transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-600/50 hover:shadow-purple-500/70"
                >
                  Go to Calculator <ArrowRight size={18} />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-6 italic">
                Astrology is a symbolic system for self-reflection and entertainment.
              </p>
            </div>
          </div>

          {/* Still show the wheel with current season */}
          <div className="w-full max-w-6xl mx-auto mb-12 opacity-60">
            <ZodiacWheel 
              autoRotate={true} 
              activeSign={getCurrentSeasonSign()} 
              moonSign={null}
              risingSign={null}
            />
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col items-center w-full min-h-[80vh] justify-center overflow-x-hidden">

      {/* Core Content Section */}
      <div className="w-full relative flex flex-col items-center justify-center">
          {userData.includeTime && (
            <p className="text-center text-sm text-primary mb-6 px-4">
              Showing your Sun, Moon, and Rising signs.
            </p>
          )}

          {/* Weekly Forecast (Premium Upsell) */}
          {userSign && (
              <div className="w-full max-w-2xl mx-auto px-4 mb-8 animate-in slide-in-from-bottom-5 fade-in">
                  <div className={`bg-[#0B1229]/80 border border-gray-800 rounded-2xl p-6 relative overflow-hidden group`}>
                      <div className="flex justify-between items-center mb-3">
                          <h3 className="text-lg font-bold text-white flex items-center gap-2">
                              <Crown size={16} className="text-[#FFC82C]" /> Weekly Forecast
                          </h3>
                      </div>
                      
                      <div className="text-gray-300 text-sm leading-relaxed min-h-[40px]">
                          {loadingForecast ? (
                              <div className="flex items-center gap-2 text-gray-500"><Loader2 size={14} className="animate-spin" /> Consulting the stars...</div>
                          ) : (
                              weeklyForecast || (
                                <div className="relative">
                                  <p className="text-gray-600">
                                      Your upcoming week holds powerful transformations in career and love. Be mindful of the moon's position on Tuesday as it...
                                  </p>
                                  <div className="absolute inset-0 flex items-center justify-center">
                                      <button onClick={loadForecast} className="bg-gradient-to-r from-purple-600 to-purple-700 text-white text-xs font-bold px-6 py-2 rounded-full border border-purple-500/50 shadow-lg shadow-purple-600/40 hover:shadow-purple-600/60 transition-all hover:from-purple-500 hover:to-purple-600">
                                          Load Extended Insight
                                      </button>
                                  </div>
                                </div>
                              )
                          )}
                      </div>
                  </div>
              </div>
          )}

          {/* Cosmic Compatibility Section (Moved Above Wheel) */}
          {userSign && (
              <div id="zodiac-tool-section" className="w-full max-w-2xl mx-auto px-4 mb-12 animate-in slide-in-from-bottom-10 fade-in duration-700 relative z-30">
                  <div className="bg-card/50 backdrop-blur-md border border-gray-800 rounded-2xl p-8 shadow-2xl">
                      <div className="flex flex-col items-center mb-6">
                        <h3 className="text-2xl font-bold text-white flex items-center justify-center gap-2 mb-2">
                            <Heart className="text-pink-500" /> Cosmic Compatibility
                        </h3>
                        {/* Cooldown Timer */}
                        <CooldownTimer 
                          isLocked={isCompatLocked}
                          timeRemaining={compatTimer}
                          animateOnUnlock={true}
                        />
                      </div>
                      
                      <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-6">
                          <div className="bg-black/30 px-4 py-2 rounded-lg border border-gray-700 text-white font-bold">
                              {userSign}
                          </div>
                          <div className="text-gray-500">+</div>
                          <div className="relative">
                              <button 
                                  onClick={() => setIsPartnerDropdownOpen(!isPartnerDropdownOpen)}
                                  className="bg-black/30 px-4 py-2 rounded-lg border border-gray-700 hover:border-purple-500 text-white outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                                  disabled={isCompatLocked}
                              >
                                  <span>{partnerSign || 'Choose Partner...'}</span>
                                  <ChevronDown size={16} className={`transition-transform duration-300 ${isPartnerDropdownOpen ? 'rotate-180' : ''}`} />
                              </button>
                              {isPartnerDropdownOpen && (
                                  <div className="absolute top-full left-0 w-full mt-2 bg-[#0B1229] border border-gray-700 rounded-lg shadow-2xl z-[100] max-h-48 overflow-y-auto animate-in fade-in zoom-in-95">
                                      <div 
                                          onClick={() => { setPartnerSign(''); setIsPartnerDropdownOpen(false); }}
                                          className="p-3 hover:bg-gray-800 cursor-pointer text-gray-300 flex items-center justify-between group"
                                      >
                                          Choose Partner...
                                      </div>
                                      {ZODIAC_ORDER.map(sign => (
                                          <div 
                                              key={sign}
                                              onClick={() => { setPartnerSign(sign); setIsPartnerDropdownOpen(false); }}
                                              className="p-3 hover:bg-gray-800 cursor-pointer text-gray-300 flex items-center justify-between group"
                                          >
                                              {sign}
                                              {partnerSign === sign && <Check size={16} className="text-purple-400" />}
                                          </div>
                                      ))}
                                  </div>
                              )}
                          </div>
                      </div>

                      <div className="flex justify-center mb-8">
                          <button 
                            onClick={handleCheckCompatibility}
                            disabled={!partnerSign || loadingCompat || isCompatLocked}
                            className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-500 hover:to-pink-400 text-white font-bold py-2 px-6 rounded-full disabled:opacity-50 disabled:cursor-not-allowed disabled:from-purple-700 disabled:to-pink-600 transition-all shadow-lg shadow-purple-600/50 hover:shadow-purple-500/70 flex items-center gap-2"
                          >
                              {loadingCompat ? <Loader2 className="animate-spin" size={18} /> : (isCompatLocked ? <Clock size={18} /> : 'Reveal Connection')}
                          </button>
                      </div>

                      {compatibility && (
                          <div className="grid gap-4 animate-in fade-in zoom-in-95">
                              <div className="bg-pink-900/20 border border-pink-900/50 p-4 rounded-xl flex items-start gap-3">
                                  <Heart className="text-pink-400 shrink-0 mt-1" size={18} />
                              <div>
                                      <div className="flex items-center gap-2 mb-1">
                                          <h4 className="font-bold text-pink-300 text-sm uppercase tracking-widest">Love</h4>
                                          <button
                                              onClick={() => handleShareSection('love', `💖 Love Compatibility: ${compatibility.love}`)}
                                              className="text-purple-400 hover:text-purple-300 transition-colors"
                                              title="Share Love Compatibility"
                                          >
                                              {copiedSection === 'love' ? <Check size={20} /> : <Share2 size={20} />}
                                          </button>
                                          {copiedSection === 'love' && <span className="text-xs text-purple-400">Copied!</span>}
                                      </div>
                                      <p className="text-gray-200 text-sm">{compatibility.love}</p>
                                  </div>
                              </div>
                              <div className="bg-blue-900/20 border border-blue-900/50 p-4 rounded-xl flex items-start gap-3">
                                  <Briefcase className="text-blue-400 shrink-0 mt-1" size={18} />
                              <div>
                                      <div className="flex items-center gap-2 mb-1">
                                          <h4 className="font-bold text-blue-300 text-sm uppercase tracking-widest">Work</h4>
                                          <button
                                              onClick={() => handleShareSection('work', `💼 Work Compatibility: ${compatibility.work}`)}
                                              className="text-purple-400 hover:text-purple-300 transition-colors"
                                              title="Share Work Compatibility"
                                          >
                                              {copiedSection === 'work' ? <Check size={20} /> : <Share2 size={20} />}
                                          </button>
                                          {copiedSection === 'work' && <span className="text-xs text-purple-400">Copied!</span>}
                                      </div>
                                      <p className="text-gray-200 text-sm">{compatibility.work}</p>
                                  </div>
                              </div>
                              <div className="bg-green-900/20 border border-green-900/50 p-4 rounded-xl flex items-start gap-3">
                                  <Users className="text-green-400 shrink-0 mt-1" size={18} />
                              <div>
                                      <div className="flex items-center gap-2 mb-1">
                                          <h4 className="font-bold text-green-300 text-sm uppercase tracking-widest">Friendship</h4>
                                          <button
                                              onClick={() => handleShareSection('friend', `🫂 Friendship Compatibility: ${compatibility.friend}`)}
                                              className="text-purple-400 hover:text-purple-300 transition-colors"
                                              title="Share Friendship Compatibility"
                                          >
                                              {copiedSection === 'friend' ? <Check size={20} /> : <Share2 size={20} />}
                                          </button>
                                          {copiedSection === 'friend' && <span className="text-xs text-purple-400">Copied!</span>}
                                      </div>
                                      <p className="text-gray-200 text-sm">{compatibility.friend}</p>
                                  </div>
                              </div>
                          </div>
                      )}
                  </div>
              </div>
          )}

          {/* The Main 3D Event */}
          <div className="w-full max-w-6xl mx-auto mb-12">
             <ZodiacWheel 
                autoRotate={true} 
                activeSign={userSign || getCurrentSeasonSign()} 
                moonSign={moonSign}
                risingSign={risingSign}
             />
          </div>

          {/* In-Article Ad for Zodiac Page */}
          <AdSpace type="in-article" slot="7716101506" className="max-w-2xl mx-auto mb-12" />

          {/* Educational Content (AdSense Compliance) - Rendered from JSON */}
          <div className="w-full max-w-5xl mx-auto px-4 mb-12">
              <div className="bg-cosmic-800/30 p-8 md:p-12 rounded-2xl border border-cosmic-700/50">
                  <div className="seo-content" dangerouslySetInnerHTML={{ __html: zodiacData.html }} />
              </div>
          </div>
      </div>
    </div>
  );
};

export default ZodiacPage;
